<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, ref, watch } from "vue";
import FormModal from "@/entries/interact-form/components/FormModal.vue";
import InitialFormView from "@/entries/interact-form/components/InitialFormView.vue";
import InteractContactFormWrapper from "@/entries/interact-form/components/InteractContactFormWrapper.vue";
import ThankYouView from "@/entries/interact-form/components/ThankYouView.vue";
import { useFormDataStore } from "@/entries/interact-form/stores/formData";
// Composables
import { useContactForm } from "@/shared/composables/useContactForm";
import { useFormNavigation } from "@/shared/composables/useFormNavigation";
import { useFormState } from "@/shared/composables/useFormState";
import { useGlobalFunctions } from "@/shared/composables/useGlobalFunctions";
import { useModalControl } from "@/shared/composables/useModalControl";
import type { InputDataItem, ProcessedFormData } from "@/shared/types";
import {
	createPrefixHandler,
	PREFIX_HANDLER_KEY,
} from "@/shared/utils/prefixHandler";

// プロップの型定義（InputDataItemを再利用）
interface Props {
	contactformId: string;
	contactformHtml: string;
	datas: InputDataItem[];
	thanksUrl?: string; // サンクスページURLを追加（オプショナル）
	disableHeightLimit?: boolean; // 高さ制限解除フラグを追加（オプショナル）
	formTitle?: string; // ACFで設定されたフォームタイトル（オプショナル）
	modalOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	modalOnly: false,
});
const formDataStore = useFormDataStore();

// InteractForm用のプレフィックスハンドラーを作成・提供
const interactPrefixHandler = createPrefixHandler(/^([a-z]+):/i);
provide(PREFIX_HANDLER_KEY, interactPrefixHandler);

// イベントエミッター
const emit = defineEmits<{
	(e: "close"): void;
	(e: "submit", data: ProcessedFormData): void;
	(e: "unmount"): void;
}>();

// Composablesの使用
const { isFormSubmitted, displayState, setFormSubmitted, setDisplayState } =
	useFormState();
const { isModalOpen, openModal, closeModal } = useModalControl();

// 加工済みのContact Form 7 HTML
const processedContactFormHtml = ref("");

/**
 * モーダルを開く関数
 *
 * 最初の質問への回答状態、現在の質問インデックス、次の質問に進む関数を
 * useModalControlのopenModal関数に渡してモーダルを開きます
 */
function handleOpenModal() {
	openModal(
		hasAnsweredFirstQuestion.value,
		formDataStore.currentQuestionIndex,
		formDataStore.nextQuestion,
		formDataStore.formDetails[0]?.type ?? "",
	);
}

function handleDocumentClick(event: MouseEvent) {
	if (!(event.target instanceof Element)) {
		return;
	}

	if (event.target.closest(".js-interact-modal-trigger")) {
		handleOpenModal();
	}
}

/**
 * モーダルを閉じる関数
 *
 * フォーム送信状態をuseModalControlのcloseModal関数に渡して
 * モーダルを閉じます
 */
function handleCloseModal() {
	closeModal(isFormSubmitted.value);
}

const {
	hasAnsweredFirstQuestion,
	handleAnswerUpdate,
	handleLastQuestionSubmit,
} = useFormNavigation(formDataStore, {
	openModalFn: handleOpenModal,
});

const redirectUrlToUse = props.thanksUrl || "/taiwa-thanks";

const {
	injectFormDataToHtml,
	setupRequiredFieldsValidation,
	setupContactForm7EventListeners,
	checkFormSubmissionStatus,
} = useContactForm(props.contactformId, props.contactformHtml, {
	redirectUrl: redirectUrlToUse, // propsから受け取ったURLを使用、未指定時はデフォルト
	hiddenFieldPrefix: "interact_",
});

// グローバル関数の設定
useGlobalFunctions(formDataStore);

/**
 * 最終的なフォーム送信処理
 *
 * Contact Form 7の送信完了後に呼び出され、以下の処理を行います:
 * 1. フォームデータを処理して整形
 * 2. 親コンポーネントに送信イベントを発火
 * 3. フォーム送信済みフラグをセット
 */
function submitForm() {
	const processedData = formDataStore.processFormData();

	// 送信イベントを発火（親コンポーネントへの通知用）
	emit("submit", processedData);

	// フォーム送信済みフラグをセット
	setFormSubmitted(true);
	// リダイレクトはuseContactForm.tsで統一処理される
}

/**
 * 回答更新のラッパー関数
 *
 * @param {number} id - 質問のID
 * @param {string | string[]} answer - 更新する回答（文字列または文字列の配列）
 */
function handleUpdateAnswer(id: number, answer: string | string[]) {
	handleAnswerUpdate(id, answer);
}

/**
 * 最後の質問の送信処理のラッパー関数
 *
 * 全ての質問に回答した後、Contact Form 7の必須フィールドの
 * バリデーションを設定します
 */
function handleSubmitLastQuestion() {
	handleLastQuestionSubmit(setupRequiredFieldsValidation);
}

/**
 * サンクスビューを表示するかどうかの計算プロパティ
 *
 * 以下の条件のいずれかを満たす場合にtrueを返します:
 * - 表示状態が'thank-you'
 * - 表示状態が'full'かつフォームが送信済み
 */
const showThankYouView = computed(
	() =>
		displayState.value === "thank-you" ||
		(displayState.value === "full" && isFormSubmitted.value),
);

/**
 * 初期フォームビューを表示するかどうかの計算プロパティ
 *
 * 以下の条件をすべて満たす場合にtrueを返します:
 * - 表示状態が'full'
 * - フォームが未送信
 * - 質問が1つ以上ある
 * - モーダルが閉じているか、最初の質問に未回答
 */
const showInitialFormView = computed(
	() =>
		displayState.value === "full" &&
		!isFormSubmitted.value &&
		!props.modalOnly &&
		formDataStore.totalQuestions > 0 &&
		(!isModalOpen.value || !hasAnsweredFirstQuestion.value),
);

/**
 * フォームモーダルを表示するかどうかのref
 *
 * 以下の条件をすべて満たす場合にtrueを返します:
 * - モーダルが開いている
 * - 最初の質問に回答済み
 * - フォームが未送信
 */
const showFormModal = ref(false);

// showFormModalの状態をcomputed値と同期
watch(
	() =>
		isModalOpen.value &&
		(props.modalOnly || hasAnsweredFirstQuestion.value) &&
		!isFormSubmitted.value,
	(newValue) => {
		showFormModal.value = newValue;
	},
	{ immediate: true },
);

/**
 * コンポーネントがマウントされたときの初期化処理
 *
 * 以下の処理を行います:
 * 1. URLのハッシュを確認し、Contact Form 7のフラグメントがあれば感謝ページへリダイレクト
 * 2. 質問データの存在確認
 * 3. ストアのデータ初期化
 * 4. Contact Form 7のHTMLを加工
 * 5. Contact Form 7のイベントリスナー設定
 * 6. フォーム送信状態の確認
 */
onMounted(() => {
	if (props.modalOnly) {
		document.addEventListener("click", handleDocumentClick);
	}

	// Contact Form 7のフラグメントがあればハッシュをクリアしてサンクスページへリダイレクト
	if (location.hash && location.hash.startsWith("#wpcf7-f")) {
		// URLからハッシュを削除してからリダイレクト
		window.history.replaceState(
			null,
			"",
			window.location.pathname + window.location.search,
		);
		window.location.replace(redirectUrlToUse);
		return;
	}
	try {
		// マウント時にURLを確認し、Contact Form 7のフラグメントがあればthanksページへ遷移
		// データが正しく渡されているか確認
		if (!props.datas || props.datas.length === 0) {
			console.warn(
				"[InteractForm] No question data provided, initializing with empty data",
			);
			// Initialize with empty data to prevent errors
			formDataStore.initializeFromInputData([], props.contactformId || "");
		} else {
			// ストアにデータ初期化を委譲（contactformIdも一緒に渡す）
			formDataStore.initializeFromInputData(
				props.datas,
				props.contactformId,
			);
		}

		// Contact Form 7のHTMLを加工
		if (props.contactformHtml) {
			processedContactFormHtml.value = injectFormDataToHtml(
				props.contactformHtml,
				formDataStore,
			);
		}

		// Contact Form 7のイベントリスナーを設定
		setupContactForm7EventListeners(submitForm);

		// マウント時にフォームの送信状態を確認
		// DOMが完全にレンダリングされた後に実行するため、少し遅延させる
		setTimeout(() => {
			checkFormSubmissionStatus(props.contactformId);
		}, 500);
	} catch (error) {
		console.error("Failed to initialize InteractForm:", error);
		// エラー状態を設定
		setDisplayState("none");
	}
});

onUnmounted(() => {
	document.removeEventListener("click", handleDocumentClick);
});

/**
 * フォームデータの変更を監視し、HTMLを再生成する
 *
 * formDataStore.formDetailsが変更されたときに、
 * Contact Form 7のHTMLを再加工します。
 * deep: trueオプションで、ネストされたオブジェクトの変更も検知します。
 */
watch(
	() => formDataStore.formDetails,
	() => {
		if (props.contactformHtml) {
			processedContactFormHtml.value = injectFormDataToHtml(
				props.contactformHtml,
				formDataStore,
			);
		}
	},
	{ deep: true },
);
</script>

<template>
  <!-- 表示状態に応じて条件分岐（計算プロパティを使用） -->
  <ThankYouView v-if="showThankYouView" />

  <!-- 通常表示（モーダルが閉じられている場合の表示） -->
  <InitialFormView v-else-if="showInitialFormView"
    :hasAnsweredFirstQuestion="hasAnsweredFirstQuestion" :isModalOpen="isModalOpen"
    :thanksUrl="redirectUrlToUse"
    :disableHeightLimit="props.disableHeightLimit"
    :formTitle="props.formTitle"
    @openModal="handleOpenModal" @updateAnswer="handleUpdateAnswer"
    @lastQuestionSubmit="handleSubmitLastQuestion" />

  <!-- FormModal（interact-form専用の復元されたコンポーネント） -->
  <FormModal v-if="showFormModal" :isOpen="showFormModal" :contactformHtml="props.contactformHtml"
    :contactformId="props.contactformId"
    @close="handleCloseModal" @updateAnswer="handleUpdateAnswer"
    @lastQuestionSubmit="handleSubmitLastQuestion">
    <template #contact-form>
      <InteractContactFormWrapper :contactformHtml="props.contactformHtml"
        :processedHtml="processedContactFormHtml" />
    </template>
  </FormModal>
</template>

<style lang="scss" scoped>
</style>
